import React from "react";
import { ChromeWindow } from "@/types";
import { X, ChevronDown, ChevronUp, GripHorizontal } from "lucide-react";
import TabCard from "./TabCard";
import { useSortable } from "@dnd-kit/sortable";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { cn, chromeTabToTab } from "@/lib/utils";

interface OpenTabsPanelProps {
  windows: ChromeWindow[];
  onClose?: () => void;
}

const OpenTabsPanel = ({ windows, onClose }: OpenTabsPanelProps) => {
  const [expandedWindows, setExpandedWindows] = React.useState<string[]>([windows[0]?.id || '']);
  const [isDragging, setIsDragging] = React.useState(false);
  const [startY, setStartY] = React.useState(0);
  const [currentY, setCurrentY] = React.useState(0);
  const panelRef = React.useRef<HTMLDivElement>(null);

  const toggleWindow = (windowId: string) => {
    setExpandedWindows(prev => 
      prev.includes(windowId)
        ? prev.filter(id => id !== windowId)
        : [...prev, windowId]
    );
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
    setCurrentY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    setCurrentY(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const diff = currentY - startY;
    if (diff > 100 && onClose) {
      onClose();
    }
  };

  const translateY = isDragging ? Math.max(0, currentY - startY) : 0;

  return (
    <>
      {/* Mobile Bottom Sheet */}
      <div className="block md:hidden">
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
          onClick={onClose}
        />
        <div 
          ref={panelRef}
          className={cn(
            "fixed bottom-0 left-0 right-0 z-50",
            "bg-background rounded-t-2xl shadow-lg",
            "h-[80vh] transition-transform duration-300 ease-in-out"
          )}
          style={{
            transform: `translateY(${translateY}px)`,
          }}
        >
          {/* Mobile Header with Drag Handle */}
          <div className="flex flex-col border-b border-border">
            <div 
              className="w-full flex flex-col items-center cursor-grab active:cursor-grabbing pt-2"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="w-12 h-1 bg-muted rounded-full mb-4" />
            </div>
            <div className="flex items-center justify-between px-4 pb-4">
              <h2 className="font-semibold text-lg text-foreground">Open Tabs</h2>
              {onClose && (
                <button 
                  onClick={onClose} 
                  className="text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-full p-1"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Mobile Content */}
          <div className="p-4 overflow-y-auto" style={{ height: 'calc(100% - 89px)' }}>
            {windows.map((window) => (
              <div key={window.id} className="mb-6 last:mb-0">
                <button
                  onClick={() => toggleWindow(window.id)}
                  className="flex items-center justify-between w-full text-left mb-2 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md p-1"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{window.name}</span>
                    <span className="text-xs text-muted-foreground">({window.tabs.length})</span>
                  </div>
                  {expandedWindows.includes(window.id) ? (
                    <ChevronUp size={16} className="text-muted-foreground" />
                  ) : (
                    <ChevronDown size={16} className="text-muted-foreground" />
                  )}
                </button>

                {expandedWindows.includes(window.id) && (
                  <SortableContext 
                    items={window.tabs.map(tab => `opentab-${window.id}-${tab.id}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {window.tabs.map((tab) => (
                        <TabCard
                          key={`opentab-${window.id}-${tab.id}`}
                          tab={chromeTabToTab(tab)}
                          collectionId={window.id}
                          isOpenTab={true}
                        />
                      ))}
                    </div>
                  </SortableContext>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Desktop Side Panel */}
      <div className="hidden md:block">
        <div 
          className="fixed top-0 right-0 w-full md:w-[400px] h-screen bg-background border-l border-border shadow-lg z-50"
        >
          {/* Desktop Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h2 className="font-semibold text-lg text-foreground">Open Tabs</h2>
            {onClose && (
              <button 
                onClick={onClose} 
                className="text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-full p-1"
              >
                <X size={18} />
              </button>
            )}
          </div>

          {/* Desktop Content */}
          <div className="p-4 overflow-y-auto" style={{ height: 'calc(100% - 73px)' }}>
            {windows.map((window) => (
              <div key={window.id} className="mb-6 last:mb-0">
                <button
                  onClick={() => toggleWindow(window.id)}
                  className="flex items-center justify-between w-full text-left mb-2 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md p-1"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{window.name}</span>
                    <span className="text-xs text-muted-foreground">({window.tabs.length})</span>
                  </div>
                  {expandedWindows.includes(window.id) ? (
                    <ChevronUp size={16} className="text-muted-foreground" />
                  ) : (
                    <ChevronDown size={16} className="text-muted-foreground" />
                  )}
                </button>

                {expandedWindows.includes(window.id) && (
                  <SortableContext 
                    items={window.tabs.map(tab => `opentab-${window.id}-${tab.id}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {window.tabs.map((tab) => (
                        <TabCard
                          key={`opentab-${window.id}-${tab.id}`}
                          tab={chromeTabToTab(tab)}
                          collectionId={window.id}
                          isOpenTab={true}
                        />
                      ))}
                    </div>
                  </SortableContext>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default OpenTabsPanel;
